import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const SelectContainer = styled.div`
  position: relative;
  display: inline-block;
  margin: 0 4px;
  font-family: inherit;
`;

const SelectTrigger = styled.div`
  padding: 2px 3px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: left;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  gap: 2px;
  &:hover {
    border-color: var(--lumiq-primary, #007bff);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const SelectOptions = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  z-index: 1005; /* Higher than DatePicker */
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  max-height: 160px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeIn 0.15s ease-out;
  text-align: left;
  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Option = styled.div`
  padding: 5px 8px;
  cursor: pointer;
  font-size: 12px;
  background: ${props => (props.isSelected ? "var(--lumiq-primary-light, #e6f7ff)" : "#fff")};
  color: ${props => (props.isSelected ? "var(--lumiq-primary, #007bff)" : "#333")};
  
  border-bottom: 1px solid #f9f9f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => (props.isSelected ? "var(--lumiq-primary-light, #e6f7ff)" : "#f5f5f5")};
  }
`;

const DateHeaderSelect = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const optionsRef = useRef(null);

  useEffect(() => {
    if (isOpen && optionsRef.current) {
      const selectedOption = optionsRef.current.querySelector('[data-selected="true"]');
      if (selectedOption) {
        // Scroll only within the dropdown, not the whole page
        const optionsContainer = optionsRef.current;
        const optionTop = selectedOption.offsetTop;
        const optionHeight = selectedOption.offsetHeight;
        const containerHeight = optionsContainer.clientHeight;
        const scrollTop = optionTop - (containerHeight / 2) + (optionHeight / 2);
        optionsContainer.scrollTop = Math.max(0, scrollTop);
      }
    }
  }, [isOpen]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

    // Find label for current value if options are objects, otherwise use value directly
    const getLabel = (val) => {
        return val; 
    }

  return (
    <SelectContainer ref={containerRef}>
      <SelectTrigger onClick={toggleOpen}>
        {getLabel(value)}
        <span>
          <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="SVGRepo_bgCarrier" stroke-width="0" />
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
            <g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M4.29289 8.29289C4.68342 7.90237 5.31658 7.90237 5.70711 8.29289L12 14.5858L18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289C20.0976 8.68342 20.0976 9.31658 19.7071 9.70711L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L4.29289 9.70711C3.90237 9.31658 3.90237 8.68342 4.29289 8.29289Z" fill="#000000" /> </g>
          </svg></span> {/* Down arrow */}
      </SelectTrigger>
      {isOpen && (
        <SelectOptions ref={optionsRef}>
          {options.map((option) => (
            <Option
              key={option}
              isSelected={option === value}
              data-selected={option === value}
              onClick={() => handleSelect(option)}
            >
              {getLabel(option)}
            </Option>
          ))}
        </SelectOptions>
      )}
    </SelectContainer>
  );
};

export default DateHeaderSelect;
